import { Component, OnInit } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { MessageService } from 'app/shared/message';
import {
  CreatePriceListGQL,
  DeletePriceListGQL,
  PriceListFragment,
  PriceListsGQL,
  UpdatePriceListGQL,
} from './graphql/price-list.generated';

@Component({
  selector: 'company-price-list-settings',
  templateUrl: './company-price-list-settings.component.html',
  styleUrls: ['./company-price-list-settings.component.scss'],
})
export class CompanyPriceListSettingsComponent implements OnInit {
  public loading = true;
  public priceLists: PriceListFragment[] = [];
  public selectedPriceListId: number;
  public selectedPriceList: PriceListFragment;
  public showCreateDialog = false;
  public showRenameDialog = false;
  public newPriceListName = '';
  public renamePriceListName = '';

  constructor(
    private priceListsGQL: PriceListsGQL,
    private createPriceListGQL: CreatePriceListGQL,
    private updatePriceListGQL: UpdatePriceListGQL,
    private deletePriceListGQL: DeletePriceListGQL,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  public ngOnInit(): void {
    this.loadPriceLists();
  }

  public loadPriceLists(selectedId?: number): void {
    this.loading = true;
    this.priceListsGQL
      .fetch(undefined, { fetchPolicy: 'network-only' })
      .pipe(first())
      .subscribe({
        next: result => {
          this.priceLists =
            result.data.company.priceLists.edges.map(edge =>
              this.toMutablePriceList(edge.node)
            ) ?? [];
          this.selectedPriceListId =
            selectedId ?? this.selectedPriceListId ?? this.priceLists[0]?.id;
          this.setSelectedPriceList();
          this.loading = false;
        },
        error: () => this.handleRequestError('Prislistorna kunde inte hämtas.'),
      });
  }

  public setSelectedPriceList(): void {
    this.selectedPriceList = this.priceLists.find(
      priceList => priceList.id === this.selectedPriceListId
    );
  }

  public createPriceList(): void {
    this.newPriceListName = '';
    this.showCreateDialog = true;
  }

  public editSelectedPriceList(): void {
    if (!this.selectedPriceList) {
      return;
    }

    this.renamePriceListName = this.selectedPriceList.name ?? '';
    this.showRenameDialog = true;
  }

  public confirmCreatePriceList(): void {
    const name = this.newPriceListName.trim();
    if (!name) {
      return;
    }

    this.createPriceListGQL
      .mutate({ name })
      .pipe(first())
      .subscribe({
        next: result => {
          const priceList = result.data.createPriceListMutation;
          this.messageService.insertDataFromMutation(priceList);
          if (!this.mutationSucceeded(priceList)) {
            return;
          }

          this.showCreateDialog = false;
          this.loadPriceLists(priceList.id);
        },
        error: () => this.handleRequestError('Prislistan kunde inte skapas.'),
      });
  }

  public confirmRenamePriceList(): void {
    if (!this.selectedPriceList) {
      return;
    }

    this.savePriceList(false, this.renamePriceListName, () => {
      this.showRenameDialog = false;
    });
  }

  public save(): void {
    if (!this.selectedPriceList) {
      return;
    }

    if ((this.selectedPriceList.activeProjectCount ?? 0) > 0) {
      this.confirmationService.confirm({
        header: 'Uppdatera projektpriser',
        message:
          'Prislistan används i planerade eller pågående projekt. Vill du uppdatera priserna i dessa projekt också?',
        acceptLabel: 'Ja',
        rejectLabel: 'Nej',
        accept: () => this.savePriceList(true),
        reject: () => this.savePriceList(false),
      });
      return;
    }

    this.savePriceList(false);
  }

  public deleteSelected(): void {
    if (!this.selectedPriceList) {
      return;
    }

    const hasLinkedContacts = (this.selectedPriceList.contactCount ?? 0) > 0;
    const hasLinkedProjects = (this.selectedPriceList.projectCount ?? 0) > 0;
    const hasLinkedRecords = hasLinkedContacts || hasLinkedProjects;

    this.confirmationService.confirm({
      header: 'Ta bort prislista',
      message: hasLinkedRecords
        ? 'Denna prislista är kopplad till en eller flera kunder eller projekt. Om du tar bort den kommer dessa kunder och projekt att återgå till standardpriserna angivna på ditt konto. Vill du ta bort prislistan?'
        : `Vill du ta bort prislistan ${this.selectedPriceList.name}?`,
      acceptLabel: hasLinkedRecords ? 'Ja' : 'Ta bort',
      rejectLabel: 'Avbryt',
      acceptButtonStyleClass: hasLinkedRecords
        ? 'p-button-success'
        : 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.deletePriceListGQL
          .mutate({ id: Number(this.selectedPriceList.id) })
          .pipe(first())
          .subscribe({
            next: result => {
              const priceList = result.data.deletePriceListMutation;
              this.messageService.insertDataFromMutation(priceList);
              if (!this.mutationSucceeded(priceList)) {
                return;
              }

              this.selectedPriceListId = undefined;
              this.loadPriceLists();
            },
            error: () =>
              this.handleRequestError('Prislistan kunde inte tas bort.'),
          });
      },
    });
  }

  private savePriceList(
    propagateToActiveProjects: boolean,
    nameOverride?: string,
    afterSuccess?: () => void
  ): void {
    const name = (nameOverride ?? this.selectedPriceList.name ?? '').trim();
    if (!name) {
      this.messageService.insertData({
        textArray: ['Prislistan måste ha ett namn.'],
        type: 'error',
      });
      return;
    }

    const userCostTypePrices = {};
    const companyCostTypePrices = {};

    this.selectedPriceList.userCostTypes.forEach(row => {
      const cost = this.parsePriceListValue(row.cost);
      if (cost === null) {
        return;
      }
      userCostTypePrices[row.companyUserCostTypeId] = cost;
    });
    this.selectedPriceList.companyCostTypes.forEach(row => {
      const procent = this.parsePriceListValue(row.procent);
      if (procent === null) {
        return;
      }
      companyCostTypePrices[row.companyCostTypeId] = procent;
    });

    if (
      Object.keys(userCostTypePrices).length !==
        this.selectedPriceList.userCostTypes.length ||
      Object.keys(companyCostTypePrices).length !==
        this.selectedPriceList.companyCostTypes.length
    ) {
      this.messageService.insertData({
        textArray: ['Prislistan innehåller ogiltiga värden.'],
        type: 'error',
      });
      return;
    }

    this.updatePriceListGQL
      .mutate({
        id: Number(this.selectedPriceList.id),
        name,
        userCostTypePrices: JSON.stringify(userCostTypePrices),
        companyCostTypePrices: JSON.stringify(companyCostTypePrices),
        propagateToActiveProjects,
      })
      .pipe(first())
      .subscribe({
        next: result => {
          const priceList = result.data.updatePriceListMutation;
          this.messageService.insertDataFromMutation(priceList);
          if (!this.mutationSucceeded(priceList)) {
            return;
          }

          this.loadPriceLists(priceList.id);
          afterSuccess?.();
        },
        error: () => this.handleRequestError('Prislistan kunde inte sparas.'),
      });
  }

  private parsePriceListValue(value: number): number | null {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private mutationSucceeded(priceList: PriceListFragment): boolean {
    return (
      !!priceList &&
      (priceList.mutationDetails ?? []).every(
        detail => detail?.mutationSucceeded !== false
      )
    );
  }

  private handleRequestError(message: string): void {
    this.loading = false;
    this.messageService.insertData({
      textArray: [message],
      type: 'error',
    });
  }

  private toMutablePriceList(priceList: PriceListFragment): PriceListFragment {
    return {
      ...priceList,
      userCostTypes: priceList.userCostTypes.map(row => ({ ...row })),
      companyCostTypes: priceList.companyCostTypes.map(row => ({ ...row })),
      mutationDetails: priceList.mutationDetails
        ? priceList.mutationDetails.map(row => ({ ...row }))
        : priceList.mutationDetails,
    };
  }
}
